/**
 * `/docs/changelog` — rendered from `changelog/*.md` at build time.
 *
 * Stage 4's `useChangelog()` hook does the Vite glob import + tiny
 * frontmatter parse. Here we render the entries, support `?tag=…`
 * filtering, and deep-link by slug via `?entry=…`.
 *
 * Markdown rendering: we keep it minimal (no marked/remark dep) —
 * paragraphs, headings, lists, inline code, bold/italic, links. The
 * author (stage 5+) writes entries that stay within that envelope.
 */
import { EmptyState } from '@/components/primitives/EmptyState';
import { Island } from '@/components/shell/Island';
import { Button } from '@/components/ui/button';
import type { ChangelogEntry } from '@/lib/queries';
import { useChangelog } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { BookOpen } from '@phosphor-icons/react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { type ReactElement, useMemo } from 'react';

type ChangelogSearch = { tag?: string; entry?: string };

export const Route = createFileRoute('/_app/docs/changelog')({
  component: ChangelogPage,
  validateSearch: (raw: Record<string, unknown>): ChangelogSearch => {
    const out: ChangelogSearch = {};
    if (typeof raw.tag === 'string' && raw.tag.length > 0) out.tag = raw.tag;
    if (typeof raw.entry === 'string' && raw.entry.length > 0) out.entry = raw.entry;
    return out;
  },
});

function ChangelogPage(): ReactElement {
  const entries = useChangelog();
  const { tag, entry: activeSlug } = useSearch({ from: '/_app/docs/changelog' });
  const navigate = useNavigate();

  const tags = useMemo(() => {
    const set = new Set<string>();
    for (const e of entries) for (const t of e.tags) set.add(t);
    return [...set].sort();
  }, [entries]);

  const visible = useMemo(() => {
    if (!tag) return entries;
    return entries.filter((e) => e.tags.includes(tag));
  }, [entries, tag]);

  function setTag(next: string | undefined) {
    navigate({
      to: '/docs/changelog',
      search: next ? { tag: next } : {},
      replace: true,
    });
  }

  return (
    <Island breadcrumb={[{ label: 'Docs', href: '/docs' }, { label: 'Changelog' }]}>
      <div className="mx-auto max-w-3xl space-y-6">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold text-[var(--color-ink)]">Changelog</h1>
          <p className="text-sm text-[var(--color-mute)]">
            Every customer-visible change we ship. Deep-link any entry via its slug — the section
            URLs are stable.
          </p>
        </header>

        {tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-[var(--color-mute)]">Filter:</span>
            <TagPill active={!tag} onClick={() => setTag(undefined)} label="All" />
            {tags.map((t) => (
              <TagPill key={t} active={tag === t} onClick={() => setTag(t)} label={t} />
            ))}
          </div>
        )}

        {visible.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title="Nothing here yet"
            description={
              tag
                ? `No entries tagged "${tag}".`
                : 'The changelog is empty. Entries land here as `.md` files shipped with the app.'
            }
            action={
              tag ? (
                <Button variant="outline" onClick={() => setTag(undefined)}>
                  Clear filter
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ol className="space-y-8">
            {visible.map((e) => (
              <Entry key={e.slug} entry={e} highlighted={e.slug === activeSlug} />
            ))}
          </ol>
        )}
      </div>
    </Island>
  );
}

function TagPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-[var(--color-line-strong)] bg-[var(--color-paper-alt)] text-[var(--color-ink)]'
          : 'border-transparent bg-[var(--color-paper-alt)] text-[var(--color-ink-soft)] hover:bg-[var(--color-line)]',
      )}
    >
      {label}
    </button>
  );
}

function Entry({
  entry,
  highlighted,
}: {
  entry: ChangelogEntry;
  highlighted: boolean;
}): ReactElement {
  return (
    <li
      id={entry.slug}
      className={cn(
        'rounded-2xl border bg-[var(--color-paper)] p-5 transition-colors',
        highlighted ? 'border-[var(--color-line-strong)]' : 'border-[var(--color-line)]',
      )}
    >
      <header className="mb-3 space-y-1">
        <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--color-mute)]">
          <time dateTime={entry.date}>{entry.date}</time>
          {entry.tags.map((t) => (
            <span
              key={t}
              className="rounded-full bg-[var(--color-paper-alt)] px-2 py-0.5 font-medium text-[var(--color-ink-soft)]"
            >
              {t}
            </span>
          ))}
        </div>
        <h2 className="text-lg font-semibold text-[var(--color-ink)]">
          <a href={`#${entry.slug}`} className="hover:underline">
            {entry.title}
          </a>
        </h2>
      </header>
      <div className="prose-sm space-y-3 text-sm leading-relaxed text-[var(--color-ink-soft)]">
        <Markdown body={entry.body} />
      </div>
    </li>
  );
}

// -----------------------------------------------------------------
// Tiny markdown renderer. Intentional feature subset:
//   - blank-line separated paragraphs
//   - `- ` lists
//   - `**bold**` / `*italic*` / `_italic_`
//   - `` `code` ``
//   - `[text](url)` links
// Keeps us off `marked`/`remark`; entries are authored within the
// envelope.
// -----------------------------------------------------------------

function Markdown({ body }: { body: string }): ReactElement {
  const blocks = body.trim().split(/\n{2,}/);
  return (
    <>
      {blocks.map((block, i) => {
        const lines = block.split('\n');
        const isList = lines.every((l) => l.trim().startsWith('- '));
        if (isList) {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: deterministic block order
            <ul key={i} className="list-disc space-y-1 pl-5">
              {lines.map((l, j) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: deterministic line order
                <li key={j}>{inline(l.replace(/^\s*-\s+/, ''))}</li>
              ))}
            </ul>
          );
        }
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: deterministic block order
          <p key={i}>{inline(block)}</p>
        );
      })}
    </>
  );
}

function inline(text: string): ReactElement[] {
  // Order matters: code first (so ** inside code stays literal), then
  // bold, italic, link. We produce a flat React fragment array.
  const parts: ReactElement[] = [];
  let remaining = text;
  let cursor = 0;
  const pattern = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*|_[^_]+_)|(\[[^\]]+\]\([^)]+\))/;

  while (remaining.length > 0) {
    const match = remaining.match(pattern);
    if (!match || match.index == null) {
      parts.push(<span key={`t-${cursor++}`}>{remaining}</span>);
      break;
    }
    if (match.index > 0) {
      parts.push(<span key={`t-${cursor++}`}>{remaining.slice(0, match.index)}</span>);
    }
    const token = match[0];
    if (token.startsWith('`')) {
      parts.push(
        <code
          key={`c-${cursor++}`}
          className="rounded-sm bg-[var(--color-paper-alt)] px-1 py-0.5 font-mono text-xs"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith('**')) {
      parts.push(
        <strong key={`b-${cursor++}`} className="font-semibold text-[var(--color-ink)]">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith('[')) {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (linkMatch) {
        parts.push(
          <a
            key={`l-${cursor++}`}
            href={linkMatch[2]}
            target="_blank"
            rel="noreferrer noopener"
            className="underline"
          >
            {linkMatch[1]}
          </a>,
        );
      }
    } else {
      const inner = token.slice(1, -1);
      parts.push(
        <em key={`i-${cursor++}`} className="italic">
          {inner}
        </em>,
      );
    }
    remaining = remaining.slice(match.index + token.length);
  }
  return parts;
}
